import { useMutation } from '@apollo/client';
import { ConfirmationDialogContent } from '@openx/components-core/lib/Dialog/ConfirmationDialog/components/ConfirmationDialogContent';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteCuratedDealBuyerDiscountDocument } from '../../../graphql/mutation/curatedDealBuyerDiscount/DeleteCuratedDealBuyerDiscount.generated';
import { GetCuratedDealBuyerDiscountListDocument } from '../../../graphql/query/curatedDealBuyerDiscount/GetCuratedDealBuyerDiscountList.generated';
import type { CuratedDealBuyerDiscountApi } from '../../../types/curatedDealBuyerDiscount';

export const useCuratedDealBuyerDiscountDelete = () => {
  const { close, open, setLoading } = useConfirmationDialog();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [deleteMutation, { loading }] = useMutation(DeleteCuratedDealBuyerDiscountDocument, {
    awaitRefetchQueries: true,
    onCompleted: () => enqueueSnackbar({ message: t('Discount successfully deleted.'), variant: 'success' }),
    refetchQueries: [GetCuratedDealBuyerDiscountListDocument],
  });

  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  const deleteCuratedDealBuyerDiscount = useCallback(
    ({ name, uid }: CuratedDealBuyerDiscountApi, onDelete?: () => void) => {
      open({
        confirmationButtonColor: 'error',
        confirmationLabel: t('Delete'),
        content: <ConfirmationDialogContent actionText="delete" name={name} objectText={t('discount')} />,
        customContent: true,
        onConfirmed: async () => {
          try {
            await deleteMutation({ variables: { uid } });
            onDelete?.();
          } catch (err) {
            // biome-ignore lint/suspicious/noConsole: logging error in console
            console.error(err);
          } finally {
            close();
          }
        },
        showExtraBtn: false,
        title: t('Delete Discount'),
      });
    },
    [close, deleteMutation, open, t]
  );

  return {
    deleteCuratedDealBuyerDiscount,
  };
};
